import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '@/contexts/RealTimeContext';
import { executionsApi } from '@/lib/api';

export interface NodeExecution {
  id: string;
  nodeId: string;
  nodeType: string;
  nodeLabel?: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  startedAt: number;
  completedAt?: number;
  duration?: number;
  inputData?: unknown;
  outputData?: unknown;
  error?: string;
  // Sub-node (tool execution) properties
  isSubNode?: boolean;
  parentNodeExecutionId?: string;
  parentNodeId?: string;
  toolName?: string;
  toolNodeType?: string; // Type of the tool node (e.g., "dataTable", "kbSearch")
  toolNodeId?: string; // Canvas node ID of the tool
}

export interface LogEntry {
  id: string;
  nodeId: string;
  level: 'log' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: number;
}

export interface LiveExecutionState {
  executionId: string | null;
  status: 'idle' | 'running' | 'success' | 'failed' | 'cancelled';
  startedAt: number | null;
  completedAt: number | null;
  nodeExecutions: NodeExecution[];
  selectedNodeId: string | null;
  logs: LogEntry[];
  isLoading: boolean;
}

interface Execution {
  id: string;
  workflow_id: string;
  status: 'running' | 'success' | 'failed' | 'cancelled';
  trigger_type: string;
  created_at: number;
  completed_at?: number;
  error?: string;
}

interface UseLiveExecutionProps {
  currentExecution: Execution | null | undefined;
}

export function useLiveExecution({ currentExecution }: UseLiveExecutionProps) {
  const [state, setState] = useState<LiveExecutionState>({
    executionId: null,
    status: 'idle',
    startedAt: null,
    completedAt: null,
    nodeExecutions: [],
    selectedNodeId: null,
    logs: [],
    isLoading: false,
  });

  const currentExecutionIdRef = useRef<string | null>(null);
  const userHasSelectedRef = useRef<boolean>(false); // Track if user manually selected a node
  const hasFetchedRef = useRef<boolean>(false); // Track if we've already fetched data for this execution
  const { subscribeToExecution, unsubscribeFromExecution, setActiveExecution } = useSocket();

  // Helper to normalize timestamp to milliseconds (defined early so it can be used in useEffect)
  const normalizeTimestamp = useCallback((ts: unknown): number => {
    if (ts === null || ts === undefined) return Date.now();

    if (typeof ts === 'number') {
      // Skip obviously invalid timestamps
      if (ts <= 0) return Date.now();
      // If it's a Unix timestamp in seconds (roughly between year 2000 and 2100), convert to ms
      // Year 2000 in seconds: ~946684800, Year 2100 in seconds: ~4102444800
      if (ts > 946684800 && ts < 10000000000) {
        return ts * 1000;
      }
      // Already in milliseconds or close enough
      return ts;
    }

    if (typeof ts === 'string') {
      // Try parsing as ISO string first
      const parsed = new Date(ts).getTime();
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
      // Try parsing as numeric string
      const num = Number(ts);
      if (!isNaN(num) && num > 0) {
        return num < 10000000000 ? num * 1000 : num;
      }
      return Date.now();
    }

    return Date.now();
  }, []);

  // Clear state and set up new execution
  useEffect(() => {
    if (currentExecution?.id) {
      // Execution changed - clear state and set up new one
      if (currentExecutionIdRef.current !== currentExecution.id) {
        currentExecutionIdRef.current = currentExecution.id;
        userHasSelectedRef.current = false; // Reset user selection tracking
        hasFetchedRef.current = false; // Reset fetch tracking

        const normalizedStartedAt = normalizeTimestamp(currentExecution.created_at);
        const normalizedCompletedAt = currentExecution.completed_at
          ? normalizeTimestamp(currentExecution.completed_at)
          : null;

        setState({
          executionId: currentExecution.id,
          status: currentExecution.status === 'running' ? 'running' : currentExecution.status,
          startedAt: normalizedStartedAt,
          completedAt: normalizedCompletedAt,
          nodeExecutions: [],
          selectedNodeId: null,
          logs: [],
          isLoading: true,
        });

        // Subscribe to SignalR updates
        setActiveExecution(currentExecution.id);
        subscribeToExecution(currentExecution.id);

        // Fetch initial state from API
        fetchExecutionState(currentExecution.id, currentExecution.status === 'running');
        hasFetchedRef.current = true;
      } else {
        // Same execution, just update status and completedAt
        const normalizedCompletedAt = currentExecution.completed_at
          ? normalizeTimestamp(currentExecution.completed_at)
          : null;

        setState(prev => ({
          ...prev,
          status: currentExecution.status === 'running' ? 'running' : currentExecution.status,
          completedAt: normalizedCompletedAt || prev.completedAt,
        }));
      }
    } else {
      // No execution - clear everything
      if (currentExecutionIdRef.current) {
        unsubscribeFromExecution(currentExecutionIdRef.current);
      }
      currentExecutionIdRef.current = null;
      userHasSelectedRef.current = false;
      hasFetchedRef.current = false;
      setActiveExecution(null);
      setState({
        executionId: null,
        status: 'idle',
        startedAt: null,
        completedAt: null,
        nodeExecutions: [],
        selectedNodeId: null,
        logs: [],
        isLoading: false,
      });
    }

    return () => {
      if (currentExecutionIdRef.current) {
        unsubscribeFromExecution(currentExecutionIdRef.current);
      }
    };
  }, [currentExecution?.id, currentExecution?.status, currentExecution?.completed_at, subscribeToExecution, unsubscribeFromExecution, setActiveExecution, normalizeTimestamp]);

  // Fetch execution state from API
  const fetchExecutionState = useCallback(async (executionId: string, isRunning: boolean, retryCount = 0) => {
    try {
      const response = await executionsApi.getExecutionState(executionId);
      const data = response.data;

      // Only update if this is still the current execution
      if (currentExecutionIdRef.current !== executionId) return;

      // Map node executions from API response
      const nodeExecutions: NodeExecution[] = (data.nodeExecutions || []).map((ne: any) => {
        const startedAt = normalizeTimestamp(ne.startedAt || ne.started_at || ne.created_at);
        const completedAt = ne.completedAt || ne.completed_at ? normalizeTimestamp(ne.completedAt || ne.completed_at) : undefined;
        const duration = ne.duration_ms || (completedAt && startedAt ? completedAt - startedAt : undefined);

        return {
          id: ne.id,
          nodeId: ne.nodeId || ne.node_id,
          nodeType: ne.nodeType || ne.node_type,
          nodeLabel: ne.nodeLabel || ne.node_label,
          status: ne.status === 'error' ? 'failed' : ne.status,
          startedAt,
          completedAt,
          duration,
          inputData: ne.input_data ? (typeof ne.input_data === 'string' ? safeJsonParse(ne.input_data) : ne.input_data) : undefined,
          outputData: ne.output_data ? (typeof ne.output_data === 'string' ? safeJsonParse(ne.output_data) : ne.output_data) : (ne.output || undefined),
          error: ne.error,
          // Sub-node (tool execution) properties - preserve from API response
          isSubNode: ne.is_sub_node || ne.isSubNode || false,
          parentNodeExecutionId: ne.parent_node_execution_id || ne.parentNodeExecutionId,
          parentNodeId: ne.parent_node_id || ne.parentNodeId,
          toolName: ne.tool_name || ne.toolName,
          toolNodeType: ne.tool_node_type || ne.toolNodeType,
          toolNodeId: ne.tool_node_id || ne.toolNodeId,
        };
      });

      // Sort by startedAt (oldest first for execution order)
      nodeExecutions.sort((a, b) => a.startedAt - b.startedAt);

      // Extract logs from execution data
      const logs: LogEntry[] = (data.executionData?.logs || []).map((log: any, index: number) => ({
        id: log.id || `log-${executionId}-${index}`,
        nodeId: log.nodeId || log.node_id || '',
        level: log.level || 'log',
        message: typeof log === 'string' ? log : (log.message || JSON.stringify(log)),
        timestamp: log.timestamp ? normalizeTimestamp(log.timestamp) : Date.now(),
      }));

      setState(prev => {
        // Only auto-select if user hasn't manually selected AND no current selection
        const shouldAutoSelect = !userHasSelectedRef.current && !prev.selectedNodeId;
        const latestNodeId = nodeExecutions.length > 0 ? nodeExecutions[nodeExecutions.length - 1].id : null;

        // Update startedAt from API response if available (more reliable than currentExecution.created_at)
        const newStartedAt = data.startedAt
          ? normalizeTimestamp(data.startedAt)
          : (data.created_at ? normalizeTimestamp(data.created_at) : prev.startedAt);

        const newCompletedAt = data.completedAt
          ? normalizeTimestamp(data.completedAt)
          : (data.completed_at ? normalizeTimestamp(data.completed_at) : prev.completedAt);

        return {
          ...prev,
          status: data.status === 'running' ? 'running' : data.status,
          startedAt: newStartedAt,
          completedAt: newCompletedAt,
          nodeExecutions,
          logs,
          isLoading: false,
          selectedNodeId: shouldAutoSelect ? latestNodeId : prev.selectedNodeId,
        };
      });
    } catch (error: any) {
      const status = error?.response?.status;
      // Retry for running executions that aren't in DB yet
      if (isRunning && status === 404 && retryCount < 5) {
        const delay = 1000 * Math.pow(1.5, retryCount);
        setTimeout(() => fetchExecutionState(executionId, isRunning, retryCount + 1), delay);
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    }
  }, [normalizeTimestamp]);

  // Handle node:started event
  const handleNodeStarted = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const nodeExecutionId = data.nodeExecutionId || `${data.nodeId}-${Date.now()}`;
    const startedAt = normalizeTimestamp(data.startedAt);

    setState(prev => {
      // Check if already exists
      if (prev.nodeExecutions.some(ne => ne.id === nodeExecutionId)) {
        return prev;
      }

      const newNode: NodeExecution = {
        id: nodeExecutionId,
        nodeId: data.nodeId,
        nodeType: data.nodeType,
        nodeLabel: data.nodeName,
        status: 'running',
        startedAt,
      };

      const updated = [...prev.nodeExecutions, newNode];
      updated.sort((a, b) => a.startedAt - b.startedAt);

      // Only auto-select if user hasn't manually selected
      const shouldAutoSelect = !userHasSelectedRef.current;

      return {
        ...prev,
        nodeExecutions: updated,
        selectedNodeId: shouldAutoSelect ? nodeExecutionId : prev.selectedNodeId,
      };
    });
  }, [normalizeTimestamp]);

  // Handle node:completed event
  const handleNodeCompleted = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const completedAt = normalizeTimestamp(data.completedAt);

    setState(prev => {
      let nodeIndex = -1;

      if (data.nodeExecutionId) {
        nodeIndex = prev.nodeExecutions.findIndex(ne => ne.id === data.nodeExecutionId);
      } else {
        // Fallback: find last occurrence of this nodeId
        nodeIndex = prev.nodeExecutions.map(ne => ne.nodeId).lastIndexOf(data.nodeId);
      }

      if (nodeIndex === -1) return prev;

      // Parse output if it's a JSON string (SignalR may serialize objects as strings)
      let outputData = data.output;
      if (typeof outputData === 'string') {
        try {
          outputData = JSON.parse(outputData);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      const updated = [...prev.nodeExecutions];
      const node = updated[nodeIndex];
      updated[nodeIndex] = {
        ...node,
        status: 'success',
        completedAt,
        duration: completedAt - node.startedAt,
        outputData,
      };

      return { ...prev, nodeExecutions: updated };
    });
  }, [normalizeTimestamp]);

  // Handle node:failed event
  const handleNodeFailed = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const completedAt = normalizeTimestamp(data.completedAt) || Date.now();

    setState(prev => {
      const nodeIndex = prev.nodeExecutions.map(ne => ne.nodeId).lastIndexOf(data.nodeId);
      if (nodeIndex === -1) return prev;

      const updated = [...prev.nodeExecutions];
      const node = updated[nodeIndex];
      updated[nodeIndex] = {
        ...node,
        status: 'failed',
        completedAt,
        duration: completedAt - node.startedAt,
        error: data.error,
      };

      // Add error log
      const newLog: LogEntry = {
        id: `log-${Date.now()}`,
        nodeId: data.nodeId,
        level: 'error',
        message: `Node failed: ${data.error}`,
        timestamp: Date.now(),
      };

      return {
        ...prev,
        nodeExecutions: updated,
        logs: [...prev.logs.slice(-499), newLog],
      };
    });
  }, [normalizeTimestamp]);

  // Handle execution:completed event
  const handleExecutionCompleted = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const completedAt = normalizeTimestamp(data.completedAt) || Date.now();

    setState(prev => ({ ...prev, status: 'success', completedAt }));

    // Fetch final state to ensure consistency
    fetchExecutionState(data.executionId, false);
  }, [fetchExecutionState, normalizeTimestamp]);

  // Handle execution:failed event
  const handleExecutionFailed = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const completedAt = normalizeTimestamp(data.completedAt) || Date.now();

    setState(prev => ({ ...prev, status: 'failed', completedAt }));
  }, [normalizeTimestamp]);

  // Handle execution:cancelled event
  const handleExecutionCancelled = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const completedAt = normalizeTimestamp(data.completedAt) || Date.now();

    setState(prev => ({ ...prev, status: 'cancelled', completedAt }));
  }, [normalizeTimestamp]);

  // Handle execution:log event (real-time log messages)
  const handleExecutionLog = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const newLog: LogEntry = {
      id: data.id || `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      nodeId: data.nodeId || '',
      level: data.level || 'log',
      message: data.message || (typeof data === 'string' ? data : JSON.stringify(data)),
      timestamp: normalizeTimestamp(data.timestamp) || Date.now(),
    };

    setState(prev => ({
      ...prev,
      logs: [...prev.logs.slice(-499), newLog], // Keep last 500 logs
    }));
  }, [normalizeTimestamp]);

  // Handle subnode:started event (tool execution within AI agent)
  const handleSubNodeStarted = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const subNodeExecutionId = data.subNodeExecutionId || `${data.toolName}-${Date.now()}`;
    const startedAt = normalizeTimestamp(data.startedAt);

    setState(prev => {
      // Check if already exists
      if (prev.nodeExecutions.some(ne => ne.id === subNodeExecutionId)) {
        return prev;
      }

      // Parse input if it's a JSON string (SignalR may serialize objects as strings)
      let inputData = data.input;
      if (typeof inputData === 'string') {
        try {
          inputData = JSON.parse(inputData);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      const newSubNode: NodeExecution = {
        id: subNodeExecutionId,
        nodeId: data.toolNodeId || data.parentNodeId,
        nodeType: data.toolNodeType || 'tool',
        nodeLabel: data.toolLabel || data.toolName, // Use friendly label if available
        status: 'running',
        startedAt,
        isSubNode: true,
        parentNodeExecutionId: data.parentNodeExecutionId,
        parentNodeId: data.parentNodeId,
        toolName: data.toolName,
        toolNodeType: data.toolNodeType, // Type of the tool node (e.g., "dataTable", "kbSearch")
        toolNodeId: data.toolNodeId, // Canvas node ID of the tool
        inputData,
      };

      // Insert the sub-node right after its parent in the list
      const parentIndex = prev.nodeExecutions.findIndex(ne => ne.id === data.parentNodeExecutionId);
      const updated = [...prev.nodeExecutions];

      if (parentIndex !== -1) {
        // Find the last sub-node of this parent to insert after
        let insertIndex = parentIndex + 1;
        while (
          insertIndex < updated.length &&
          updated[insertIndex].parentNodeExecutionId === data.parentNodeExecutionId
        ) {
          insertIndex++;
        }
        updated.splice(insertIndex, 0, newSubNode);
      } else {
        updated.push(newSubNode);
      }

      return {
        ...prev,
        nodeExecutions: updated,
      };
    });
  }, [normalizeTimestamp]);

  // Handle subnode:completed event (tool execution completed)
  const handleSubNodeCompleted = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const completedAt = normalizeTimestamp(data.completedAt);

    setState(prev => {
      const subNodeIndex = prev.nodeExecutions.findIndex(ne => ne.id === data.subNodeExecutionId);
      if (subNodeIndex === -1) return prev;

      const updated = [...prev.nodeExecutions];
      const subNode = updated[subNodeIndex];
      // Parse output if it's a JSON string (SignalR may serialize objects as strings)
      let outputData = data.output;
      if (typeof outputData === 'string') {
        try {
          outputData = JSON.parse(outputData);
        } catch {
          // Keep as string if not valid JSON
        }
      }
      // Extract actual output - it may be nested in output.output if wrapped
      if (outputData && typeof outputData === 'object' && 'output' in outputData) {
        outputData = outputData.output;
      }

      updated[subNodeIndex] = {
        ...subNode,
        status: 'success',
        completedAt,
        duration: data.durationMs || (completedAt - subNode.startedAt),
        outputData,
      };

      return { ...prev, nodeExecutions: updated };
    });
  }, [normalizeTimestamp]);

  // Handle subnode:failed event (tool execution failed)
  const handleSubNodeFailed = useCallback((event: Event) => {
    const data = (event as CustomEvent).detail;
    const currentExecId = currentExecutionIdRef.current;

    if (!currentExecId || data.executionId !== currentExecId) return;

    const completedAt = normalizeTimestamp(data.failedAt) || Date.now();

    setState(prev => {
      const subNodeIndex = prev.nodeExecutions.findIndex(ne => ne.id === data.subNodeExecutionId);
      if (subNodeIndex === -1) return prev;

      const updated = [...prev.nodeExecutions];
      const subNode = updated[subNodeIndex];
      updated[subNodeIndex] = {
        ...subNode,
        status: 'failed',
        completedAt,
        duration: data.durationMs || (completedAt - subNode.startedAt),
        error: data.error,
      };

      return { ...prev, nodeExecutions: updated };
    });
  }, [normalizeTimestamp]);

  // Subscribe to window events
  useEffect(() => {
    if (!state.executionId) return;

    window.addEventListener('node:started', handleNodeStarted as EventListener);
    window.addEventListener('node:completed', handleNodeCompleted as EventListener);
    window.addEventListener('node:failed', handleNodeFailed as EventListener);
    window.addEventListener('execution:completed', handleExecutionCompleted as EventListener);
    window.addEventListener('execution:failed', handleExecutionFailed as EventListener);
    window.addEventListener('execution:cancelled', handleExecutionCancelled as EventListener);
    window.addEventListener('execution-log', handleExecutionLog as EventListener);
    window.addEventListener('subnode:started', handleSubNodeStarted as EventListener);
    window.addEventListener('subnode:completed', handleSubNodeCompleted as EventListener);
    window.addEventListener('subnode:failed', handleSubNodeFailed as EventListener);

    return () => {
      window.removeEventListener('node:started', handleNodeStarted as EventListener);
      window.removeEventListener('node:completed', handleNodeCompleted as EventListener);
      window.removeEventListener('node:failed', handleNodeFailed as EventListener);
      window.removeEventListener('execution:completed', handleExecutionCompleted as EventListener);
      window.removeEventListener('execution:failed', handleExecutionFailed as EventListener);
      window.removeEventListener('execution:cancelled', handleExecutionCancelled as EventListener);
      window.removeEventListener('execution-log', handleExecutionLog as EventListener);
      window.removeEventListener('subnode:started', handleSubNodeStarted as EventListener);
      window.removeEventListener('subnode:completed', handleSubNodeCompleted as EventListener);
      window.removeEventListener('subnode:failed', handleSubNodeFailed as EventListener);
    };
  }, [
    state.executionId,
    handleNodeStarted,
    handleNodeCompleted,
    handleNodeFailed,
    handleExecutionCompleted,
    handleExecutionFailed,
    handleExecutionCancelled,
    handleExecutionLog,
    handleSubNodeStarted,
    handleSubNodeCompleted,
    handleSubNodeFailed,
  ]);

  // Select a node (user action)
  const selectNode = useCallback((nodeId: string | null) => {
    if (nodeId !== null) {
      userHasSelectedRef.current = true; // Mark that user has made a selection
    }
    setState(prev => ({ ...prev, selectedNodeId: nodeId }));
  }, []);

  // Clear user selection (allows auto-selection to resume)
  const clearUserSelection = useCallback(() => {
    userHasSelectedRef.current = false;
    // Auto-select the latest running or most recent node
    setState(prev => {
      const runningNode = prev.nodeExecutions.find(ne => ne.status === 'running');
      const latestNode = prev.nodeExecutions[prev.nodeExecutions.length - 1];
      return {
        ...prev,
        selectedNodeId: runningNode?.id || latestNode?.id || null,
      };
    });
  }, []);

  // Get the selected node
  const selectedNode = state.nodeExecutions.find(ne => ne.id === state.selectedNodeId) || null;

  // Get nodes in reverse order (latest first for display)
  // But preserve parent-child grouping so sub-nodes appear after their parent
  const nodesLatestFirst = (() => {
    const nodes = state.nodeExecutions;
    const result: NodeExecution[] = [];

    // Group nodes: main nodes with their sub-nodes
    const mainNodes: NodeExecution[] = [];
    const subNodesByParent = new Map<string, NodeExecution[]>();

    for (const node of nodes) {
      const isSubNodeCheck = node.isSubNode && node.parentNodeExecutionId;
      if (isSubNodeCheck) {
        const subs = subNodesByParent.get(node.parentNodeExecutionId!) || [];
        subs.push(node);
        subNodesByParent.set(node.parentNodeExecutionId!, subs);
      } else {
        mainNodes.push(node);
      }
    }

    // Reverse main nodes (latest first), then append their sub-nodes in order
    for (const mainNode of [...mainNodes].reverse()) {
      result.push(mainNode);
      const subs = subNodesByParent.get(mainNode.id);
      if (subs) {
        // Keep sub-nodes in chronological order (oldest first within their group)
        result.push(...subs);
      }
    }

    // Include any orphaned sub-nodes at the end (shouldn't happen, but safety)
    const includedSubNodeIds = new Set(result.filter(n => n.isSubNode).map(n => n.id));
    for (const subs of subNodesByParent.values()) {
      for (const sub of subs) {
        if (!includedSubNodeIds.has(sub.id)) {
          result.push(sub);
        }
      }
    }

    return result;
  })();

  // Calculate stats
  const stats = {
    completedCount: state.nodeExecutions.filter(ne => ne.status === 'success').length,
    failedCount: state.nodeExecutions.filter(ne => ne.status === 'failed').length,
    runningCount: state.nodeExecutions.filter(ne => ne.status === 'running').length,
    totalCount: state.nodeExecutions.length,
  };

  // Calculate duration with fallback to node-based calculation
  const calculateDuration = (): number => {
    if (!state.startedAt) return 0;

    const endTime = state.completedAt ||
      (state.status === 'running' ? Date.now() :
        (state.nodeExecutions.length > 0
          ? Math.max(...state.nodeExecutions.map(ne => ne.completedAt || ne.startedAt))
          : Date.now()));

    let duration = endTime - state.startedAt;

    // If duration is negative (timestamp format mismatch), calculate from node executions
    if (duration < 0 && state.nodeExecutions.length > 0) {
      const firstNodeStart = Math.min(...state.nodeExecutions.map(ne => ne.startedAt));
      const lastNodeEnd = Math.max(...state.nodeExecutions.map(ne => ne.completedAt || ne.startedAt));
      duration = lastNodeEnd - firstNodeStart;
    }

    return Math.max(0, duration); // Ensure non-negative
  };

  const duration = calculateDuration();

  return {
    ...state,
    nodesLatestFirst,
    selectedNode,
    selectNode,
    clearUserSelection,
    stats,
    duration,
  };
}

// Safe JSON parse helper
function safeJsonParse(str: string): unknown {
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
}
